// Keep realtime receipts newer than in-flight HTTP snapshots. This cache is
// local to one mounted inbox; the server remains authoritative for counts.
export const createStaffUnreadState = (messageLimit = 10000) => {
  const seen = new Map();
  const counts = new Map();
  let version = 0;
  const serverRevision = value => Number.isSafeInteger(value) && value >= 0 ? value : null;
  const messageKey = message => message?.conversation_id && message?.id
    ? `${message.conversation_id}|${message.id}` : null;
  const remember = message => {
    const key = messageKey(message);
    if (!key) return;
    const read = seen.get(key) === true || message.read_by_staff === true;
    seen.delete(key);
    seen.set(key, read);
    if (seen.size > messageLimit) seen.delete(seen.keys().next().value);
  };
  const update = (roomId, count, revision = null) => {
    if (!roomId || !Number.isInteger(count) || count < 0) return false;
    const previous = counts.get(roomId);
    const nextRevision = serverRevision(revision);
    if (previous?.revision !== null && previous?.revision !== undefined
      && (nextRevision === null || nextRevision < previous.revision)) return false;
    if (nextRevision !== null && previous?.revision === nextRevision && count > previous.count) return false;
    if (previous?.count === count && previous?.revision === nextRevision) return true;
    counts.set(roomId, { count, revision: nextRevision, version: ++version });
    return true;
  };
  return {
    version: () => version,
    rememberMessages: messages => (messages || []).forEach(remember),
    acceptMessage(message) {
      const key = messageKey(message);
      const fresh = Boolean(key && !seen.has(key));
      remember(message);
      return fresh && message.sender_role === 'player' && message.read_by_staff !== true;
    },
    confirmRead(roomId, ids) {
      (ids || []).forEach(id => remember({ conversation_id: roomId, id, read_by_staff: true }));
    },
    count: (roomId, fallback = 0) => counts.get(roomId)?.count ?? (Number(fallback) || 0),
    update,
    snapshot(room, startedAt) {
      const latest = counts.get(room.id);
      if (serverRevision(room.staff_unread_revision) !== null || latest?.revision !== null && latest?.revision !== undefined) {
        const accepted = update(room.id, Number(room.staff_unread), room.staff_unread_revision);
        const current = counts.get(room.id);
        return accepted ? room : { ...room, staff_unread: current?.count ?? room.staff_unread,
          staff_unread_revision: current?.revision };
      }
      if (latest && latest.version > startedAt) return { ...room, staff_unread: latest.count };
      const count = Number(room.staff_unread);
      if (Number.isInteger(count) && count >= 0) counts.set(room.id, { count, revision: null, version });
      return room;
    },
    changedSince: (roomId, startedAt) => (counts.get(roomId)?.version || 0) > startedAt,
  };
};

export const confirmedStaffUnread = response => {
  const count = response?.staff_unread;
  if (response?.ok === false || count === undefined || count === null
    || !Number.isInteger(Number(count)) || Number(count) < 0) {
    throw new Error('Invalid read acknowledgement');
  }
  return Number(count);
};
